import { type NdsSegmentedButtonsItem } from '@/components/NewDesignSystem/NdsSegmentedButtons/NdsSegmentedButtons.interfaces'
import type { GanttPeriodicityFilterOption } from '@/contexts/shared/gantts/Gantt.interfaces'
import type { RestApiSchema } from '@/interfaces/Api.interfaces'
import type { TranslatedFields } from '@/interfaces/Language.interfaces'
import type {
  BaseSearchFilters,
  SearchFilterField,
  SearchFilterValue,
} from '@/contexts/shared/search-filter/SearchFilter.interfaces'
import { type PerimeterType } from '@/contexts/organization/Organization.interfaces'

import type { OwnerType } from '@/contexts/shared/owners/Owners.interfaces'
import type { DatatableSorting } from '@/components/NewDesignSystem/NdsDatatable/NdsDatatable.interfaces'
import type { BeneficiaryPerimeter } from '@/contexts/perimeters/Perimeters.interfaces'

export enum ActionPlanDisplayViews {
  GANTT = 'gantt',
  TRELLO = 'trello',
  LIST = 'list',
  WORKLOAD = 'workload',
  STATS = 'stats',
}

export interface DisplayTypesButtonItem extends NdsSegmentedButtonsItem {
  value: ActionPlanDisplayViews
}

export interface GetActionPlansResponse extends RestApiSchema<ActionPlan[]> {
  all_actions_summary?: AllActionsSummary | null
}

export type GetMeasureBeneficiariesResponse = RestApiSchema<BeneficiaryPerimeter[]>

export interface AllActionsSummary {
  id: string
  name: TranslatedFields
  completion: number
  nb_actions: number
  workload: number
  cost: number
  nb_anomalies: number
}

export interface ActionPlan {
  attachment: {
    id: number
    identifier: string
    name: TranslatedFields
    type: string
  }
  completion: number
  cost: number
  id: number
  name: TranslatedFields
  actionsCount: number
  anomaliesCount: number
  groupsCount: number
  workload: number
  is_hidden: boolean
}

export interface LegacyActionPlan {
  id: number
  name: string
  description: string
  identifier: string
  name_fr: string
  description_fr: string | null
  name_en: string
  description_en: string | null
  priority: number | null
  initial_cost: number
  initial_workload: number
  spent_cost?: number | null
  spent_workload?: number | null
  completion: number
  nb_projects: number
  local_perimeter: object | null
  local_tag: {
    id: number
    tag: string
    identifier: string
    is_root: boolean
    name: string
    name_en: string
    name_fr: string
    type: string
  } | null
  nb_remediations: number
  warning: number
  delete_right: boolean
  view_right: boolean
  edit_right: boolean
  color?: string | null
  group: object | null
  user: object | null
  ug: {
    icon: string
    name: string | null
    initials: string
  }
  weather: object | null
  weather_wop: object | null
  prev_weather: object | null
  prev_weather_wop: object | null
  prefix_remediation_id: string | null
  remediations_perimeters: number[]
  common_tags: number[]
  is_hidden: boolean | null
}

export interface ActionPlanFilters {
  showFinished: boolean
  showCancelled: boolean
  priorityLevelIds: number[]
  ganttPeriodicity: GanttPeriodicityFilterOption
}

export interface ActionPlanFiltersStore {
  customerId: number
  actionPlanId: string
  filters: ActionPlanFilters
}

export interface ActionPlansListFilters {
  showAnomaly: boolean
  showHidden: boolean
}

export interface ActionPlansListFiltersStore {
  customerId: number
  filters: ActionPlansListFilters
}

export interface LocalPerimeter {
  id: number
  name: string
  edit_right: boolean
  view_right: boolean
  identifier: string
  type: string
}

export interface LocalTag {
  id: number
  tag: string
  identifier: string
  is_root: boolean
  name: string
}

export interface ActionPlanOptionLegacy {
  id: number
  name: string
  identifier: string
  edit_right: boolean
  local_perimeter: LocalPerimeter | null
  local_tag: LocalTag | null
  is_hidden: boolean | null
}

export interface ProgramLegacy {
  id: number
  name: string
  description?: string
  identifier?: string
  name_fr?: string
  name_en?: string
}

export interface ActionGroup {
  id: number | null
  identifier: string | null
  program_id: number | null
  name: string | null
}

// This interface represents the structure of ActionGroup as received from the API. Not in generated interfaces because legacy endpoint is not in swagger.
export interface ActionGroupApi {
  id: number | null
  identifier: string | null
  program_id: number | null
  name: string | null
}

export enum ActionStatus {
  TO_PLAN = 'TO_PLAN',
  PLANNED = 'PLANNED',
  ONGOING = 'ONGOING',
  STANDBY = 'STANDBY',
  TO_REVIEW = 'TO_REVIEW',
  FINISHED = 'FINISHED',
  CANCELLED = 'CANCELLED',
}

export enum ActionAnomalyType {
  TO_PLAN = 'TO_PLAN',
  PLANNED_LATE = 'PLANNED_LATE',
  ONGOING_LATE = 'ONGOING_LATE',
  OVERLOADED = 'OVERLOADED',
  EXTRA_COST = 'EXTRA_COST',
  IN_REVIEW_LATE = 'IN_REVIEW_LATE',
}

export enum ActionFilterAnomalyType {
  WITH = 'with_anomaly',
  WITHOUT = 'without_anomaly',
}

export interface Action {
  id: number
  project_name: TranslatedFields
  identifier: string
  manager?: ActionManager
  name: TranslatedFields
  priority: number
  perimeter: ActionPerimeter
  status: ActionStatus
  target_date?: string | null
  anomalies: ActionAnomalyType[]
  actionType: ActionType
}

export interface ActionManager {
  label?: string | null
  type?: OwnerType | null
}

export interface ActionPerimeter {
  name: TranslatedFields
  type: PerimeterType
}

export type ProgramId = number

export type GetActionsResponse = RestApiSchema<Action[]>

export interface ActionFilterManager {
  id: number
  type: OwnerType
}

export interface ActionFilters extends BaseSearchFilters<string | number | boolean | ActionFilterManager> {
  perimeters: SearchFilterField<number>
  priorities: SearchFilterField<number>
  groups: SearchFilterField<number>
  statuses: SearchFilterField<ActionStatus>
  managers: SearchFilterField<ActionFilterManager>
  withAnomaly: SearchFilterField<boolean>
  types: SearchFilterField<string>
  sources: SearchFilterField<string>
  labels: SearchFilterField<number>
  categories: SearchFilterField<number>
}

export type ActionsViewQueryParams = {
  perimeters?: string | string[]
  priorities?: string | string[]
  groups?: string | string[]
  statuses?: string | string[]
  actionTypes?: string | string[]
  sources?: string | string[]
  managersUsers?: string | string[]
  managersGroups?: string | string[]
  labels?: string | string[]
  categories?: string | string[]
  withAnomalies?: string
  withoutAnomalies?: string
  sortColumnId?: string
  sortOrderBy?: string
}

export interface GetActionFilterResponse {
  perimeters: SearchFilterValue[]
  groups: SearchFilterValue[]
  priorities: SearchFilterValue[]
  managers: SearchFilterValue<OwnerType>[]
  statuses: SearchFilterValue[]
  isAnomaly: SearchFilterValue[]
  actionType: SearchFilterValue[]
  source: SearchFilterValue[]
  labels: SearchFilterValue[]
  categories: SearchFilterValue[]
}

export interface SearchActionPayload {
  search: string | null
  pagination: {
    page?: number
    per_page?: number
  }
  filter: ActionFilterPayload
  sort?: DatatableSorting
}

export interface ActionFilterManagerType {
  id: number
  type: OwnerType
}

export interface ActionFilterPayload {
  perimeters?: SearchFilterField<number>
  groups?: SearchFilterField<number>
  priorities?: SearchFilterField<number>
  statuses?: SearchFilterField<string>
  sources?: SearchFilterField<string>
  withAnomaly?: SearchFilterField<boolean>
  managers?: SearchFilterField<ActionFilterManagerType>
  types?: SearchFilterField<string>
  labels?: SearchFilterField<number>
  categories?: SearchFilterField<number>
  actionPlanId?: number
}

export enum ActionPlanEvent {
  CLEAR = 'CLEAR',
  ADD_GROUP = 'ADD_GROUP',
  EDIT_ACTION_PLAN = 'EDIT_ACTION_PLAN',
  SEND_REMINDER = 'SEND_REMINDER',
  ACTIONS_MAPPING = 'ACTIONS_MAPPING',
}

export enum ActionPlanColumn {
  IDENTIFIER = 'IDENTIFIER',
  NAME = 'NAME',
  PERIMETER = 'PERIMETER',
  GROUP = 'GROUP',
  MANAGER = 'MANAGER',
  PRIORITY = 'PRIORITY',
  STATUS = 'STATUS',
  TARGET_DATE = 'TARGET_DATE',
}

export interface ActionsFiltersStore {
  customerId: number
  actionPlanId: string
  filters: ActionFilters
}

export interface ActionPlanFilterStore {
  customerId: number
  actionPlanId: string
  filters?: ActionFilters | null
  sort?: DatatableSorting | null
}

export enum ActionType {
  IMPLEMENTATION = 'IMPLEMENTATION',
  IMPROVEMENT = 'IMPROVEMENT',
  SIMPLE = 'SIMPLE',
}

export enum ActionTypeLegacy {
  SIMPLE = 'SIMPLE',
  EXISTING = 'EXISTING',
  BUILD = 'BUILD',
  UPGRADE = 'UPGRADE',
}
